import React, { useEffect } from 'react';
import { useWebhook } from '../hooks/useWebhook';
import { AnalyticsEventData } from '../types';

interface EventTrackerProps {
  trackPageViews?: boolean;
  trackButtonClicks?: boolean;
  customEvents?: Array<{
    name: string;
    selector: string;
    event: string;
  }>;
  onEventTracked?: (eventData: AnalyticsEventData) => void;
}

export const EventTracker: React.FC<EventTrackerProps> = ({
  trackPageViews = true,
  trackButtonClicks = true,
  customEvents = [],
  onEventTracked
}) => {
  const { sendAnalyticsEvent } = useWebhook({
    webhookType: 'analytics',
    onSuccess: (response) => {
      console.log('Event tracked successfully:', response);
    },
    onError: (error) => {
      console.error('Event tracking failed:', error);
    }
  });

  useEffect(() => {
    if (trackPageViews) {
      // 페이지 조회 이벤트
      const pageViewEvent: AnalyticsEventData = {
        event: 'page_view',
        data: {
          page: window.location.pathname,
          title: document.title,
          referrer: document.referrer,
          userAgent: navigator.userAgent,
        }
      };
      
      sendAnalyticsEvent(pageViewEvent);
      onEventTracked?.(pageViewEvent);
    }
  }, [trackPageViews, sendAnalyticsEvent, onEventTracked]);

  useEffect(() => {
    if (trackButtonClicks) {
      const handleButtonClick = (event: Event) => {
        const target = event.target as HTMLElement;
        if (target.tagName === 'BUTTON' || target.closest('button')) {
          const buttonName = target.textContent?.trim() || target.getAttribute('data-track') || 'unknown_button';
          
          const buttonClickEvent: AnalyticsEventData = {
            event: 'button_click',
            data: {
              button: buttonName,
              page: window.location.pathname,
              action: 'click',
            }
          };
          
          sendAnalyticsEvent(buttonClickEvent);
          onEventTracked?.(buttonClickEvent);
        }
      };

      document.addEventListener('click', handleButtonClick);
      return () => document.removeEventListener('click', handleButtonClick);
    }
  }, [trackButtonClicks, sendAnalyticsEvent, onEventTracked]);

  useEffect(() => {
    // 커스텀 이벤트 추적
    customEvents.forEach(({ name, selector, event: eventType }) => {
      const elements = document.querySelectorAll(selector);
      
      const handleCustomEvent = (e: Event) => {
        const target = e.target as HTMLElement;
        const customEvent: AnalyticsEventData = {
          event: name,
          data: {
            action: eventType,
            element: selector,
            page: window.location.pathname,
            value: target instanceof HTMLInputElement ? Number(target.value) || 0 : undefined,
          }
        };
        
        sendAnalyticsEvent(customEvent);
        onEventTracked?.(customEvent);
      };

      elements.forEach(element => {
        element.addEventListener(eventType, handleCustomEvent);
      });

      return () => {
        elements.forEach(element => {
          element.removeEventListener(eventType, handleCustomEvent);
        });
      };
    });
  }, [customEvents, sendAnalyticsEvent, onEventTracked]);

  return null; // 이 컴포넌트는 UI를 렌더링하지 않음
}; 